/**
 * HoFundingBanner - Escrow funding CTA for the homeowner project detail.
 * Shown while the project has unfunded milestones (awaiting_funding, or a
 * top-up when partially funded). Runs the same backend flow as the funding
 * banner on the professional project page: create order, Cashfree checkout
 * when the provider needs it, then backend verification.
 */

import * as React from "react";
import { toast } from "sonner";
import { launchCashfreeCheckout } from "../../services/cashfree-checkout";
import {
	fundProject,
	type Milestone,
	verifyProjectFunding,
} from "../../services/projects-api.service";
import {
	Btn,
	Eyebrow,
	FloatPanel,
	formatINR as fmt,
	Icn,
	ItalicAccent,
} from "./design-primitives";

interface HoFundingBannerProps {
	projectId: string;
	milestones: Milestone[];
	/**
	 * The milestones an open funding order covers, from the escrow rows it left
	 * `pending`. The backend resumes such an order only for a request that names
	 * exactly its milestones and refuses any other set, so while one is open this
	 * is the only funding request the banner can offer.
	 */
	inFlightMilestoneIds?: string[];
	/** Parent renders the banner only when funding is expected; the banner
	 * additionally hides itself when no milestone is fundable. */
	visible: boolean;
	accent?: string;
	onFunded: () => void | Promise<void>;
}

export function HoFundingBanner({
	projectId,
	milestones,
	inFlightMilestoneIds = [],
	visible,
	accent = "var(--color-dashboard-accent)",
	onFunded,
}: HoFundingBannerProps) {
	const unfunded = React.useMemo(
		() =>
			milestones.filter(
				(m) => m.fundingStatus === "unfunded" && (m.paymentAmount ?? 0) > 0,
			),
		[milestones],
	);

	const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
	const [funding, setFunding] = React.useState(false);
	const [statusMessage, setStatusMessage] = React.useState<string | null>(null);

	// The open order, and what it is for. Only fundable milestones are counted:
	// a row whose milestone has already been funded is one `finalizeProjectFunding`
	// settled and the sweep has not looked at yet, and there is nothing to resume
	// for it.
	const inFlight = React.useMemo(
		() => unfunded.filter((m) => inFlightMilestoneIds.includes(m.id)),
		[unfunded, inFlightMilestoneIds],
	);
	const resuming = inFlight.length > 0;

	// Selection defaults to every fundable milestone, and is recomputed only
	// when that set itself changes (after a top-up refetch), so unchecking rows
	// survives unrelated re-renders.
	const unfundedKey = React.useMemo(
		() => unfunded.map((m) => m.id).join(","),
		[unfunded],
	);
	React.useEffect(() => {
		setSelectedIds(unfundedKey ? unfundedKey.split(",") : []);
	}, [unfundedKey]);

	if (!visible || unfunded.length === 0) return null;

	const totalToFund = unfunded.reduce(
		(sum, m) => sum + (m.paymentAmount ?? 0),
		0,
	);
	const selectedTotal = unfunded
		.filter((m) => selectedIds.includes(m.id))
		.reduce((sum, m) => sum + (m.paymentAmount ?? 0), 0);
	// What the reopen asks the reader to pay: the open order's own total, which is
	// the sum of the milestones it covers — one row apiece, at their amounts — so
	// the figure on the button is the one the checkout will ask for.
	const inFlightTotal = inFlight.reduce(
		(sum, m) => sum + (m.paymentAmount ?? 0),
		0,
	);

	async function handleFund(ids: string[]) {
		setFunding(true);
		setStatusMessage("Opening secure checkout…");
		try {
			const result = await fundProject(
				projectId,
				ids.length > 0 ? ids : undefined,
			);
			if (!result.success || !result.fundingSession) {
				setStatusMessage(null);
				toast.error(result.error || "Failed to fund project");
				return;
			}

			const { fundingSession } = result;

			if (fundingSession.provider === "cashfree") {
				if (!fundingSession.paymentSessionId) {
					setStatusMessage(null);
					toast.error("Checkout is unavailable right now. Please try again.");
					return;
				}
				setStatusMessage("Complete the payment in the checkout window…");
				const checkoutResult = await launchCashfreeCheckout(
					fundingSession.paymentSessionId,
				);
				if (checkoutResult.status === "cancelled") {
					setStatusMessage(null);
					toast.error("Payment was not completed");
					return;
				}
			}

			setStatusMessage("Verifying payment…");
			const verification = await verifyProjectFunding(
				projectId,
				fundingSession.providerOrderId,
			);

			if (verification.success && verification.verified) {
				setStatusMessage(null);
				toast.success("Funds secured in escrow");
				await onFunded();
			} else if (verification.success) {
				setStatusMessage(
					"Payment is pending confirmation. We'll update this once the payment is verified.",
				);
				toast.error("Payment is pending confirmation");
			} else {
				setStatusMessage(null);
				toast.error(verification.error || "Could not verify payment");
			}
		} catch {
			setStatusMessage(null);
			toast.error("Failed to fund project");
		} finally {
			setFunding(false);
		}
	}

	const checkboxRowStyle: React.CSSProperties = {
		fontFamily: "var(--font-body)",
		fontSize: 12.5,
		display: "flex",
		alignItems: "center",
		gap: 8,
		cursor: "pointer",
		color: "var(--color-dashboard-muted-dark)",
		padding: "2px 0",
	};

	return (
		<section
			style={{ maxWidth: 1240, margin: "0 auto", padding: "0 48px 20px" }}
		>
			<FloatPanel elev={2} padding={22}>
				<div
					style={{
						display: "flex",
						alignItems: "flex-start",
						gap: 14,
						flexWrap: "wrap",
					}}
				>
					<Icn
						name="wallet"
						size={18}
						color={accent}
						style={{ marginTop: 3, flexShrink: 0 }}
					/>
					<div style={{ flex: 1, minWidth: 240 }}>
						<Eyebrow>
							{resuming
								? "PAYMENT IN PROGRESS"
								: "FUND ESCROW · WORK BEGINS ONCE FUNDED"}
						</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 22,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "8px 0 6px",
							}}
						>
							{resuming ? (
								<>
									{fmt(inFlightTotal)}{" "}
									<ItalicAccent size={22} color={accent}>
										awaiting the provider
									</ItalicAccent>
								</>
							) : (
								<>
									{fmt(totalToFund)} to hold,{" "}
									<ItalicAccent size={22} color={accent}>
										released by you
									</ItalicAccent>
								</>
							)}
						</h2>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12.5,
								lineHeight: 1.5,
								color: "var(--color-dashboard-muted-dark)",
								margin: 0,
							}}
						>
							{resuming
								? "A payment for these milestones is already open with the payment provider. Reopen it to finish, or reload this page once the provider has confirmed it — a payment that is still open cannot be replaced by one for a different set of milestones."
								: "Your money sits in Finishd escrow. Your designer starts work once it's funded, and each milestone is paid out only when you release it."}
						</p>
						{statusMessage && (
							<p
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 12,
									color: "var(--color-dashboard-accent-dark)",
									margin: "10px 0 0",
								}}
							>
								{statusMessage}
							</p>
						)}

						<div style={{ marginTop: 14 }}>
							{!resuming && (
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 10,
										marginBottom: 6,
									}}
								>
									<button
										type="button"
										onClick={() => setSelectedIds(unfunded.map((m) => m.id))}
										style={{
											border: 0,
											background: "transparent",
											padding: 0,
											cursor: "pointer",
											fontFamily: "var(--font-body)",
											fontSize: 11,
											fontWeight: 600,
											color: "var(--color-dashboard-muted)",
											textDecoration: "underline",
										}}
									>
										Select all
									</button>
									<span
										style={{
											color: "var(--color-dashboard-faint-text)",
											fontSize: 11,
										}}
									>
										|
									</span>
									<button
										type="button"
										onClick={() => setSelectedIds([])}
										style={{
											border: 0,
											background: "transparent",
											padding: 0,
											cursor: "pointer",
											fontFamily: "var(--font-body)",
											fontSize: 11,
											fontWeight: 600,
											color: "var(--color-dashboard-muted)",
											textDecoration: "underline",
										}}
									>
										Clear
									</button>
								</div>
							)}
							{(resuming ? inFlight : unfunded).map((milestone) => {
								const cells = (
									<>
										<span style={{ flex: 1 }}>{milestone.title}</span>
										<span
											style={{
												fontFamily: "var(--font-mono)",
												fontSize: 11,
												color: "var(--color-dashboard-text)",
												fontVariantNumeric: "tabular-nums",
											}}
										>
											{fmt(milestone.paymentAmount ?? 0)}
										</span>
									</>
								);
								// A row of the reopen carries no control: the milestones to reopen
								// are the order's own, so there is nothing to choose, and a label
								// with nothing in it to check is not one.
								return resuming ? (
									<div key={milestone.id} style={checkboxRowStyle}>
										{cells}
									</div>
								) : (
									<label key={milestone.id} style={checkboxRowStyle}>
										<input
											type="checkbox"
											checked={selectedIds.includes(milestone.id)}
											onChange={(e) => {
												if (e.target.checked) {
													setSelectedIds([...selectedIds, milestone.id]);
												} else {
													setSelectedIds(
														selectedIds.filter((id) => id !== milestone.id),
													);
												}
											}}
											style={{ accentColor: accent }}
										/>
										{cells}
									</label>
								);
							})}
						</div>
					</div>
					<div style={{ textAlign: "right", flexShrink: 0 }}>
						<Btn
							variant="primary"
							size="md"
							disabled={funding || (!resuming && selectedIds.length === 0)}
							onClick={() =>
								void handleFund(
									resuming ? inFlight.map((m) => m.id) : selectedIds,
								)
							}
						>
							{funding
								? "Funding…"
								: resuming
									? "Resume payment"
									: selectedIds.length === unfunded.length
										? `Fund ${fmt(selectedTotal)}`
										: `Fund selected ${fmt(selectedTotal)}`}
						</Btn>
						{!resuming && (
							<div
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 10.5,
									color: "var(--color-dashboard-muted)",
									marginTop: 6,
								}}
							>
								{selectedIds.length} of {unfunded.length} milestones selected
							</div>
						)}
					</div>
				</div>
			</FloatPanel>
		</section>
	);
}
